/**
 * Bloco "BOMs — estimado × executivo (delta)" do Detalhe do Ambiente
 * (Lote A — auditoria §2.2 / I-7 · brief bloco (d)).
 *
 * Fonte: GET /api/ambientes-producao/{id}/bom. 3 colunas:
 *   - ESTIMADO — baseline de venda (metadata.composicao do orçamento):
 *     chapas (m²), bordas (m) e ferragens (un), com custo quando presente.
 *   - EXECUTIVO — chip de origem do lote (PharuCut | manual) + sobra% +
 *     contadores ativos/reposições/canceladas (os 3 sinais reais).
 *   - DELTA — só de chapas (D2.2): verde dentro do estimado, warning
 *     quando excede, danger acima de LIMIAR_DELTA_DANGER_PCT; fontes do
 *     delta explícitas (reposições/cancelamentos/sobra).
 *
 * Estados honestos (modelo por fase — nada colapsa num "—" genérico):
 *   - sem executivo → "Plano executivo definido na Otimização" (futuro
 *     normal, itálico neutro — NÃO é erro);
 *   - `estimado_indisponivel` → aviso com o motivo (ex.: carteira
 *     importada sem composição);
 *   - delta só quando os dois BOMs existem.
 *
 * Preço: os campos de custo vêm AUSENTES do serializer sem `preco:view` —
 * o bloco só monta colunas de R$ quando o payload traz custo E o usuário
 * tem a permissão (nunca coluna de R$ vazia).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import { formatarReais } from "../../../services/financeiro";
import { bomAmbiente, reprocessarConsumo } from "../../../services/producaoV2";
import {
  Card,
  ErrorMsg,
  Pill,
  SecondaryButton,
  SectionTitle,
  WarningMsg,
} from "../../Orcamentos/styles";
import {
  LinhaErro,
  MiniStat,
  MiniStatGrid,
  Mono,
  NotaDiscreta,
  Shimmer,
  TabelaFixa,
  TituloBloco,
  fmtNum,
  fmtPct,
} from "../_shared/ui";
import { usePodeVerPrecos } from "../_shared/usePodeVerPrecos";

/** Acima deste excesso percentual sobre o estimado, o delta vira danger. */
const LIMIAR_DELTA_DANGER_PCT = 10;

const ORIGEM_LOTE_CHIP = {
  dashcut: { label: "PharuCut", tone: "info" },
  manual: { label: "manual — sem otimização de nesting", tone: "neutral" },
  externo: { label: "manual — sem otimização de nesting", tone: "neutral" },
  cad_direto: { label: "CAD direto", tone: "neutral" },
};

const FONTE_DELTA_LABELS = {
  reposicoes: "reposições",
  cancelamentos: "cancelamentos",
  canceladas: "cancelamentos",
  sobra: "sobra do lote",
};

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const GridBom = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Coluna = styled.div`
  min-width: 0;
`;

/** Estado "futuro normal" do brief: itálico neutro, sem alarme. */
const EstadoFuturo = styled.p`
  font-size: 12.5px;
  font-style: italic;
  color: var(--ink-3);
  margin: 4px 0 0;
`;

const LinhaLote = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
  font-size: 13px;
  color: var(--ink-2);
`;

const LinhaFontes = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 10px;
  font-size: 11px;
  color: var(--ink-3);
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Quantidade + unidade do item do estimado (m2 | metros | qtd). */
function qtdDoItem(item) {
  if (item?.m2 != null) return { valor: item.m2, unidade: "m²" };
  if (item?.metros != null) return { valor: item.metros, unidade: "m" };
  if (item?.qtd != null) return { valor: item.qtd, unidade: "un" };
  return { valor: null, unidade: "" };
}

function nomeDoItem(item) {
  return item?.produto_nome || item?.material || item?.nome || "–";
}

/** Payload trouxe ALGUM campo de custo? (sem preco:view o serializer omite) */
function payloadTemCusto(dados) {
  const listas = [
    dados?.estimado?.chapas,
    dados?.estimado?.materiais,
    dados?.estimado?.bordas,
    dados?.estimado?.ferragens,
    dados?.executivo?.chapas,
    Array.isArray(dados?.executivo?.materiais_ativos)
      ? dados.executivo.materiais_ativos
      : null,
    dados?.delta?.chapas,
  ];
  return listas.some(
    (lista) =>
      Array.isArray(lista) &&
      lista.some(
        (item) =>
          item &&
          Object.entries(item).some(
            ([chave, valor]) =>
              (chave === "custo" || chave.startsWith("custo_")) &&
              valor != null,
          ),
      ),
  );
}

/** Contagem tolerante: aceita número OU lista (usa o tamanho). */
function contagem(valor) {
  if (Array.isArray(valor)) return valor.length;
  const n = Number(valor);
  return Number.isFinite(n) ? n : null;
}

function toneDelta(deltaM2, deltaPct) {
  const d = Number(deltaM2);
  if (!Number.isFinite(d) || d <= 0) return "success";
  const pct = Number(deltaPct);
  if (Number.isFinite(pct) && pct > LIMIAR_DELTA_DANGER_PCT) return "danger";
  return "warning";
}

/** "+1,2" | "−0,3" | "0" (sinal explícito — delta sem sinal é ambíguo). */
function fmtDeltaAssinado(valor, casas = 2) {
  const n = Number(valor);
  if (!Number.isFinite(n) || n === 0) return "0";
  return `${n > 0 ? "+" : "−"}${fmtNum(Math.abs(n), casas)}`;
}

function chipOrigem(origem) {
  const chave = String(origem || "").toLowerCase();
  return (
    ORIGEM_LOTE_CHIP[chave] || {
      label: origem || "origem –",
      tone: "neutral",
    }
  );
}

function rotuloFonte(fonte) {
  if (fonte && typeof fonte === "object") {
    return fonte.label || fonte.descricao || fonte.tipo || "–";
  }
  return FONTE_DELTA_LABELS[String(fonte).toLowerCase()] || String(fonte);
}

// ---------------------------------------------------------------------------
// Colunas
// ---------------------------------------------------------------------------

function TabelaMateriais({ itens, mostrarCusto }) {
  return (
    <TabelaFixa>
      <colgroup>
        {mostrarCusto ? (
          <>
            <col style={{ width: "46%" }} />
            <col style={{ width: "24%" }} />
            <col style={{ width: "30%" }} />
          </>
        ) : (
          <>
            <col style={{ width: "60%" }} />
            <col style={{ width: "40%" }} />
          </>
        )}
      </colgroup>
      <thead>
        <tr>
          <th>Material</th>
          <th>Qtd</th>
          {mostrarCusto && <th>Custo</th>}
        </tr>
      </thead>
      <tbody>
        {itens.map((item, idx) => {
          const { valor, unidade } = qtdDoItem(item);
          const nome = nomeDoItem(item);
          return (
            <tr key={`${nome}-${idx}`}>
              <td title={nome}>{nome}</td>
              <td>
                <Mono>
                  {valor == null ? "–" : `${fmtNum(valor, 2)} ${unidade}`}
                </Mono>
              </td>
              {mostrarCusto && (
                <td>
                  <Mono>
                    {item?.custo == null ? "–" : formatarReais(item.custo)}
                  </Mono>
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </TabelaFixa>
  );
}

function SecaoEstimado({ titulo, itens, mostrarCusto }) {
  if (!Array.isArray(itens) || itens.length === 0) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <TituloBloco>{titulo}</TituloBloco>
      <TabelaMateriais itens={itens} mostrarCusto={mostrarCusto} />
    </div>
  );
}

function ColunaEstimado({ dados, mostrarCusto }) {
  const estimado = dados?.estimado;
  // Backend real entrega `chapas`; `materiais` fica como retrocompat do
  // contrato combinado antes da implementação.
  const chapasEstimado = estimado?.chapas || estimado?.materiais || [];
  const indisponivel = dados?.estimado_indisponivel;
  const vazio =
    estimado &&
    !chapasEstimado.length &&
    !(estimado.bordas || []).length &&
    !(estimado.ferragens || []).length;

  return (
    <Coluna>
      <TituloBloco>
        BOM estimado{" "}
        <Pill $tone="neutral" title="Agregado do metadata.composicao do orçamento de origem">
          baseline de venda
        </Pill>
      </TituloBloco>
      {indisponivel ? (
        <WarningMsg style={{ alignItems: "flex-start" }}>
          Estimado indisponível
          {typeof indisponivel === "string" ? ` — ${indisponivel}` : ""}.
        </WarningMsg>
      ) : !estimado || vazio ? (
        <EstadoFuturo>
          Sem BOM estimado — o baseline nasce com a composição do orçamento.
        </EstadoFuturo>
      ) : (
        <>
          <SecaoEstimado
            titulo="Chapas"
            itens={chapasEstimado}
            mostrarCusto={mostrarCusto}
          />
          <SecaoEstimado
            titulo="Bordas"
            itens={estimado.bordas}
            mostrarCusto={mostrarCusto}
          />
          <SecaoEstimado
            titulo="Ferragens"
            itens={estimado.ferragens}
            mostrarCusto={mostrarCusto}
          />
        </>
      )}
    </Coluna>
  );
}

function ColunaExecutivo({ dados, mostrarCusto }) {
  const executivo = dados?.executivo;

  if (!executivo) {
    return (
      <Coluna>
        <TituloBloco>BOM executivo</TituloBloco>
        <EstadoFuturo>Plano executivo definido na Otimização.</EstadoFuturo>
      </Coluna>
    );
  }

  const lote = executivo.lote || {};
  const origem = chipOrigem(lote.origem);
  // Backend real: contadores em `totais.pecas_*` e linhas em `chapas`
  // (m2_ativo/custo_ativo); shape antigo do contrato fica como fallback.
  const totais = executivo.totais || {};
  const ativos = totais.pecas_ativas ?? contagem(executivo.materiais_ativos);
  const reposicoes = totais.pecas_reposicao ?? contagem(executivo.reposicoes);
  const canceladas = totais.pecas_canceladas ?? contagem(executivo.canceladas);
  const linhasBrutas = Array.isArray(executivo.chapas)
    ? executivo.chapas
    : Array.isArray(executivo.materiais_ativos)
      ? executivo.materiais_ativos
      : null;
  const linhasAtivas = linhasBrutas
    ? linhasBrutas.map((c) => ({
        ...c,
        m2: c.m2 ?? c.m2_ativo,
        custo: c.custo ?? c.custo_ativo,
      }))
    : null;

  return (
    <Coluna>
      <TituloBloco>BOM executivo</TituloBloco>
      <LinhaLote>
        {lote.numero && <Mono>{lote.numero}</Mono>}
        <Pill $tone={origem.tone} title={`Origem do plano: ${origem.label}`}>
          {origem.label}
        </Pill>
        <span title="Sobra percentual registrada no plano de corte do lote">
          sobra{" "}
          <Mono>
            {lote.sobra_percentual == null
              ? "–"
              : fmtPct(lote.sobra_percentual)}
          </Mono>
        </span>
      </LinhaLote>

      <MiniStatGrid style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        <MiniStat title="Materiais das peças ativas do plano executivo">
          <div className="lbl">Ativos</div>
          <div className="val">{ativos == null ? "–" : ativos}</div>
        </MiniStat>
        <MiniStat title="Peças refeitas — consomem material além do estimado">
          <div className="lbl">Reposições</div>
          <div
            className="val"
            style={
              Number(reposicoes) > 0
                ? { color: "var(--warning-text)" }
                : undefined
            }
          >
            {reposicoes == null ? "–" : reposicoes}
          </div>
        </MiniStat>
        <MiniStat title="Peças canceladas — saem do consumo executivo">
          <div className="lbl">Canceladas</div>
          <div className="val">{canceladas == null ? "–" : canceladas}</div>
        </MiniStat>
      </MiniStatGrid>

      {linhasAtivas && linhasAtivas.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <TituloBloco>Materiais ativos</TituloBloco>
          <TabelaMateriais itens={linhasAtivas} mostrarCusto={mostrarCusto} />
        </div>
      )}
    </Coluna>
  );
}

function ColunaDelta({ dados, mostrarCusto }) {
  const delta = dados?.delta;
  const chapas = useMemo(
    () => (Array.isArray(delta?.chapas) ? delta.chapas : []),
    [delta],
  );
  const prontos = Boolean(dados?.estimado) && Boolean(dados?.executivo);

  const custoDeltaTotal = useMemo(() => {
    let soma = 0;
    let algum = false;
    for (const linha of chapas) {
      if (linha?.custo_delta != null) {
        soma += Number(linha.custo_delta) || 0;
        algum = true;
      }
    }
    return algum ? soma : null;
  }, [chapas]);

  return (
    <Coluna>
      <TituloBloco>Delta (execução − estimado)</TituloBloco>
      {!prontos || chapas.length === 0 ? (
        <EstadoFuturo>
          Delta disponível quando estimado e executivo existem.
        </EstadoFuturo>
      ) : (
        <>
          <TabelaFixa>
            <colgroup>
              <col style={{ width: "32%" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "17%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "22%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Material</th>
                <th title="m² estimado (baseline de venda)">Est.</th>
                <th title="m² executivo das peças ativas">Ativo</th>
                <th title="m² consumido em reposições">Rep.</th>
                <th title="Δ m² (execução − estimado)">Δ</th>
              </tr>
            </thead>
            <tbody>
              {chapas.map((linha, idx) => {
                const nome = linha?.material || "–";
                const tone = toneDelta(linha?.delta_m2, linha?.delta_pct);
                const custoDelta =
                  mostrarCusto && linha?.custo_delta != null
                    ? formatarReais(linha.custo_delta)
                    : null;
                const dicaDelta = `Δ ${fmtDeltaAssinado(linha?.delta_m2)} m² (${
                  linha?.delta_pct == null ? "—" : fmtPct(linha.delta_pct)
                })${custoDelta ? ` · ${custoDelta}` : ""}`;
                return (
                  <tr key={`${nome}-${idx}`}>
                    <td title={nome}>{nome}</td>
                    <td>
                      <Mono>{fmtNum(linha?.m2_estimado, 2)}</Mono>
                    </td>
                    <td>
                      <Mono>{fmtNum(linha?.m2_executivo_ativo, 2)}</Mono>
                    </td>
                    <td>
                      <Mono>{fmtNum(linha?.m2_reposicoes, 2)}</Mono>
                    </td>
                    <td title={dicaDelta}>
                      <Pill $tone={tone}>
                        <Mono>
                          {fmtDeltaAssinado(linha?.delta_m2)}
                          {linha?.delta_pct != null
                            ? ` (${fmtPct(linha.delta_pct, 0)})`
                            : ""}
                        </Mono>
                      </Pill>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TabelaFixa>

          {(delta?.fontes || []).length > 0 && (
            <LinhaFontes>
              Fontes do delta:
              {delta.fontes.map((fonte, idx) => (
                <Pill key={`fonte-${idx}`} $tone="neutral">
                  {rotuloFonte(fonte)}
                </Pill>
              ))}
            </LinhaFontes>
          )}

          {mostrarCusto && custoDeltaTotal != null && (
            <NotaDiscreta>
              Impacto em custo:{" "}
              <Mono
                style={
                  custoDeltaTotal > 0
                    ? { color: "var(--danger-text)" }
                    : { color: "var(--success-text)" }
                }
              >
                {custoDeltaTotal > 0 ? "+" : ""}
                {formatarReais(custoDeltaTotal)}
              </Mono>
            </NotaDiscreta>
          )}
        </>
      )}
    </Coluna>
  );
}


// ---------------------------------------------------------------------------
// Consumo real (Fase 5.1) — o que SAIU do estoque
// ---------------------------------------------------------------------------

function BlocoConsumoReal({ dados, mostrarCusto, ambienteId, onReprocessado }) {
  const { request } = useAuth();
  const consumo = dados?.consumo_real;
  const itens = useMemo(
    () => (Array.isArray(consumo?.itens) ? consumo.itens : []),
    [consumo],
  );
  const semBaixa = useMemo(
    () =>
      Array.isArray(consumo?.etapas_consumidoras_sem_baixa)
        ? consumo.etapas_consumidoras_sem_baixa
        : [],
    [consumo],
  );

  if (!consumo) return null;

  return (
    <div style={{ marginTop: 18 }}>
      <TituloBloco>
        Consumido do estoque{" "}
        <Pill $tone="neutral" title="Movimentos de saída gerados pelas etapas de produção">
          baixa real
        </Pill>
      </TituloBloco>

      {semBaixa.length > 0 && (
        <WarningMsg
          style={{ alignItems: "flex-start", marginBottom: 10, gap: 10 }}
        >
          <span style={{ flex: 1 }}>
            {semBaixa.join(", ")}{" "}
            {semBaixa.length === 1 ? "concluiu" : "concluíram"} sem baixar
            estoque. Normalmente falta o BOM do CAD, ou o produto é chapa (CH)
            sem m² por unidade cadastrado. Corrija em Suprimentos › Produtos e
            reprocesse.
          </span>
          <AsyncButton
            variant="secondary"
            onClick={async () => {
              const r = await reprocessarConsumo(request, ambienteId);
              if (onReprocessado) await onReprocessado();
              if (!r?.baixados) {
                throw new Error(
                  r?.motivos?.[0] ||
                    "Nada foi baixado — o motivo continua o mesmo.",
                );
              }
            }}
            successMessage="Consumo reprocessado."
          >
            Reprocessar consumo
          </AsyncButton>
        </WarningMsg>
      )}

      {itens.length === 0 ? (
        <EstadoFuturo>
          Nada consumido ainda — a baixa acontece quando Corte, Bordo e
          Separação concluem.
        </EstadoFuturo>
      ) : (
        <TabelaFixa>
          <colgroup>
            <col style={{ width: mostrarCusto ? "46%" : "62%" }} />
            <col style={{ width: "20%" }} />
            <col style={{ width: "12%" }} />
            {mostrarCusto && <col style={{ width: "22%" }} />}
          </colgroup>
          <thead>
            <tr>
              <th>Material</th>
              <th style={{ textAlign: "right" }}>Quantidade</th>
              <th style={{ textAlign: "right" }}>Mov.</th>
              {mostrarCusto && <th style={{ textAlign: "right" }}>Custo</th>}
            </tr>
          </thead>
          <tbody>
            {itens.map((item) => (
              <tr key={item.produto_id}>
                <td className="ell" title={`${item.codigo || ""} ${item.nome}`.trim()}>
                  {item.nome}
                </td>
                <td style={{ textAlign: "right" }}>
                  {item.quantidade} {item.unidade}
                </td>
                <td style={{ textAlign: "right" }}>{item.movimentos}</td>
                {mostrarCusto && (
                  <td style={{ textAlign: "right" }}>
                    {formatarReais(item.custo)}
                  </td>
                )}
              </tr>
            ))}
            {mostrarCusto && consumo.custo_total != null && (
              <tr>
                <td colSpan={3} style={{ fontWeight: 600 }}>
                  Total consumido
                </td>
                <td style={{ textAlign: "right", fontWeight: 600 }}>
                  {formatarReais(consumo.custo_total)}
                </td>
              </tr>
            )}
          </tbody>
        </TabelaFixa>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bloco
// ---------------------------------------------------------------------------

export default function BomBloco({ ambienteId, gatilhoRecarga }) {
  const { request } = useAuth();
  const podeVerPrecos = usePodeVerPrecos();

  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  const carregar = useCallback(async () => {
    setErro(null);
    setCarregando(true);
    try {
      setDados(await bomAmbiente(request, ambienteId));
    } catch (err) {
      setErro(err?.message || "Não foi possível carregar os BOMs.");
    } finally {
      setCarregando(false);
    }
  }, [request, ambienteId]);

  // Recarrega quando o Detalhe substitui o ambiente (cancelamento em
  // cascata muda as canceladas do executivo, por exemplo).
  useEffect(() => {
    carregar();
  }, [carregar, gatilhoRecarga]);

  // Sem preco:view o serializer omite os custos; o gate local cobre o caso
  // do payload trazer custo (ex.: cache) — nunca coluna de R$ vazia.
  const mostrarCusto = podeVerPrecos && payloadTemCusto(dados);

  return (
    <Card>
      <SectionTitle>BOMs — estimado × executivo × consumido</SectionTitle>

      {carregando && dados === null && !erro && <Shimmer $h={180} />}

      {erro && (
        <LinhaErro>
          <ErrorMsg style={{ flex: 1 }}>
            Não foi possível carregar os BOMs. {erro}
          </ErrorMsg>
          <SecondaryButton type="button" onClick={carregar}>
            Tentar de novo
          </SecondaryButton>
        </LinhaErro>
      )}

      {!erro && dados !== null && (
        <>
          <GridBom>
            <ColunaEstimado dados={dados} mostrarCusto={mostrarCusto} />
            <ColunaExecutivo dados={dados} mostrarCusto={mostrarCusto} />
            <ColunaDelta dados={dados} mostrarCusto={mostrarCusto} />
          </GridBom>
          <BlocoConsumoReal
            dados={dados}
            mostrarCusto={mostrarCusto}
            ambienteId={ambienteId}
            onReprocessado={carregar}
          />
          <NotaDiscreta>
            Delta calculado pelos sinais reais de execução (reposições,
            cancelamentos e sobra do lote) — o executivo não é cópia do
            estimado. O consumo é o que de fato saiu do estoque.
          </NotaDiscreta>
        </>
      )}
    </Card>
  );
}
